// src/pages/user/Vouchers.js
import React, { useState, useEffect } from 'react';
import { userAPI } from '../../services/api';
import Loading from '../../components/common/Loading';
import { QRCodeSVG } from 'qrcode.react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const Vouchers = () => {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');
  const [error, setError] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    loadVouchers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadVouchers = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await userAPI.getVouchers(filter);
      // defensive: accept data.data or data
      const payload = data?.data ?? data ?? [];
      setVouchers(Array.isArray(payload) ? payload : []);
    } catch (err) {
      console.error('Erreur chargement vouchers:', err);
      setError('Impossible de charger les vouchers. Réessayez plus tard.');
      setVouchers([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', text: 'Actif' },
      used: { color: 'bg-gray-100 text-gray-800', text: 'Utilisé' },
      expired: { color: 'bg-red-100 text-red-800', text: 'Expiré' }
    };
    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', text: status || 'Inconnu' };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${config.color}`} aria-hidden>
        {config.text}
      </span>
    );
  };

  const copyCode = async (code) => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Copy failed', err);
      // fallback: select + execCommand (old)
      const textarea = document.createElement('textarea');
      textarea.value = code;
      document.body.appendChild(textarea);
      textarea.select();
      try { document.execCommand('copy'); setCopiedCode(code); setTimeout(() => setCopiedCode(null), 2000); }
      catch (e) { /* ignore */ }
      textarea.remove();
    }
  };

  const printVoucher = (voucher) => {
    // create printable HTML - includes code and basic info; QR may not be included as SVG serialized
    const printable = `
      <html>
        <head>
          <title>Voucher - ${voucher.code || ''}</title>
          <meta charset="utf-8" />
          <style>
            body { font-family: Arial, Helvetica, sans-serif; padding: 24px; color: #111; }
            .container { max-width: 800px; margin: 0 auto; border: 1px solid #e5e7eb; padding: 24px; border-radius: 8px; }
            .code { font-family: monospace; font-size: 28px; font-weight: 700; color: #0f172a; margin: 12px 0; }
            .meta { color: #374151; margin-bottom: 8px; }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>Voucher</h1>
            <p class="meta">Deal: ${escapeHtml(voucher.dealId?.title || '—')}</p>
            <p class="meta">Marchand: ${escapeHtml(voucher.merchantId?.businessName || '—')}</p>
            <div class="code">${escapeHtml(voucher.code || '')}</div>
            <p class="meta">Expire le: ${formatDate(voucher.expirationDate)}</p>
            <p class="meta">Statut: ${escapeHtml(voucher.status || '')}</p>
          </div>
        </body>
      </html>
    `;
    const w = window.open('', '_blank', 'noopener,noreferrer');
    if (!w) {
      alert('Impossible d\'ouvrir la fenêtre d\'impression (bloquée par le navigateur).');
      return;
    }
    w.document.open();
    w.document.write(printable);
    w.document.close();
    // wait a bit for render then print
    setTimeout(() => {
      w.focus();
      w.print();
      // optionally close: w.close();
    }, 400);
  };

  const formatDate = (d) => {
    try {
      if (!d) return '—';
      return format(new Date(d), 'dd MMMM yyyy', { locale: fr });
    } catch {
      return d;
    }
  };

  const escapeHtml = (unsafe = '') =>
    String(unsafe)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  if (loading) return <Loading />;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Mes Vouchers</h2>

        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg"
          aria-label="Filtrer les vouchers"
        >
          <option value="active">Actifs</option>
          <option value="used">Utilisés</option>
          <option value="expired">Expirés</option>
        </select>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded">{error}</div>
      )}

      {vouchers.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-600">Aucun voucher "{filter}"</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {vouchers.map((voucher) => {
            const id = voucher._id ?? voucher.id ?? voucher.code ?? Math.random().toString(36).slice(2, 9);
            return (
              <div key={id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="font-bold text-lg truncate" title={voucher.dealId?.title || ''}>
                    {voucher.dealId?.title || 'Deal'}
                  </h3>
                  {getStatusBadge(voucher.status)}
                </div>

                <div className="flex flex-col md:flex-row items-center md:items-stretch justify-center my-4 space-y-4 md:space-y-0 md:space-x-6">
                  <div className="flex-shrink-0">
                    <QRCodeSVG
                      value={voucher.code || ''}
                      size={160}
                      role="img"
                      aria-label={`QR code pour le voucher ${voucher.code || ''}`}
                    />
                  </div>

                  <div className="text-center md:text-left">
                    <p className="text-sm text-gray-600 mb-1">Code du voucher</p>
                    <p className="text-2xl font-bold text-primary-600 font-mono">{voucher.code || '—'}</p>

                    <div className="mt-3 flex items-center justify-center md:justify-start space-x-2">
                      <button
                        type="button"
                        onClick={() => copyCode(voucher.code)}
                        className="px-3 py-2 bg-gray-100 rounded hover:bg-gray-200 text-sm"
                        aria-label={`Copier le code ${voucher.code}`}
                      >
                        {copiedCode === voucher.code ? 'Copié !' : 'Copier le code'}
                      </button>

                      <button
                        type="button"
                        onClick={() => printVoucher(voucher)}
                        className="px-3 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 text-sm"
                        aria-label={`Imprimer le voucher ${voucher.code}`}
                      >
                        Télécharger / Imprimer
                      </button>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Marchand:</span>
                    <span className="font-semibold">{voucher.merchantId?.businessName || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Expire le:</span>
                    <span className="font-semibold">{formatDate(voucher.expirationDate)}</span>
                  </div>
                  {voucher.usedAt && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Utilisé le:</span>
                      <span className="font-semibold">{format(new Date(voucher.usedAt), 'dd/MM/yyyy à HH:mm', { locale: fr })}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Vouchers;
